import { useCallback, useContext, useEffect, useMemo, useState } from "react"

import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material"

import LoadingButton from "@mui/lab/LoadingButton"

import { createTheme, ThemeProvider } from "@mui/material/styles"
import { ptBR as corePtBR } from "@mui/material/locale"

import {
  AddCircle as AddCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material"

import { constructorContext } from "../Settings/Builder"

import {
  DataGridPro,
  ptBR,
  GridToolbarContainer,
  gridClasses,
  GridToolbarColumnsButton,
  GridToolbarFilterButton
} from "@mui/x-data-grid-pro"

import { platformIcon } from "../Report/NewDetailedRegister/contact_graph_exception"
import { getSession } from "../../auth"

import ConfirmationDialogCaptcha from "../Dialogs/ConfimationDialogCaptcha"

import api from "../../api"
import Loading from "../Loading"
import useNotification from "../Notification"

const theme = createTheme({
  palette: {
    primary: { main: '#21446C' },
  },
}, ptBR, corePtBR);

const VoiceIvrSelector = ({ entrypoint, setEntrypoint, voiceIvrs, setVoiceIvrs }) => {
  const foundIvr = useMemo(() => {
    return voiceIvrs?.find(v =>
      v.mcdu?.some(item => item.mcdu === entrypoint.mcdu)
    )
  }, [voiceIvrs, entrypoint.mcdu])

  const handleIvrChange = (event) => {
    const newIvrId = event.target.value
    setEntrypoint(prev => ({
      ...prev,
      ivr_id: newIvrId,
      ivr_module_id: null,
    }))
  }

  const handleModuleChange = (event) => {
    setEntrypoint(prev => ({
      ...prev,
      ivr_module_id: event.target.value
    }))
  }

  const selectedIvr = voiceIvrs?.find(v => v.id === entrypoint.ivr_id) || foundIvr

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <FormControl fullWidth size="small">
        <InputLabel>Vivre</InputLabel>
        <Select
          label="Vivre"
          value={selectedIvr?.id ?? ""}
          onChange={handleIvrChange}
        >
          {voiceIvrs?.map(ivr => (
            <MenuItem key={ivr.id} value={ivr.id}>
              {ivr.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedIvr?.modules?.length > 0 && (
        <FormControl fullWidth size="small">
          <InputLabel>Módulo Inicial</InputLabel>
          <Select
            label="Módulo Inicial"
            value={entrypoint.ivr_module_id ?? ""}
            onChange={handleModuleChange}
          >
            {selectedIvr.modules.map(mod => (
              <MenuItem key={mod.id} value={mod.id}>
                {mod.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  )
}

const Entrypoints = () => {
	const { activeTab } = useContext(constructorContext)

  const [ agents, setAgents ] = useState([])
  const [ builders, setBuilders ] = useState([])
  const [ chatRows, setChatRows ] = useState([])
  const [ entrypoint, setEntrypoint ] = useState(null)
  const [ loading, setLoading ] = useState(false)
  const [ voiceEntrypoint, setVoiceEntrypoint ] = useState(null)
  const [ voiceRows, setVoiceRows ] = useState([])
  const [ voiceIvrs, setVoiceIvrs ] = useState([])
  const [ toDelete, setToDelete ] = useState(null)

  const deleteMcdu = async (obj) => {
    const enterpriseId = getSession().profile?.enterprise?.id
    const response = await api.delete(`api/enterprises/${enterpriseId}/mcdu/chat/${obj.id}/`)
    if (response.status === 204) {
      useNotification("Sucesso", `A entrada ${obj.mcdu} foi removida com sucesso.`, "success")
      setChatRows((prev) => prev.filter((item) => item.id !== obj.id))
      setToDelete(null)
    }
  }

  const chatColumns = [
    {
      field: "channel",
      headerName: "Canal",
      flex: 1,
      renderCell: (params) => {
        return (
          <Box display="flex" gap={2}>
            {platformIcon(params.row.entrypoint) ?? platformIcon("INSTANTCHAT")}
            {params.row.mcdu}
          </Box>
        )
      }
    },
    {
      field: "builder_id",
      headerName: "ChatBot",
      flex: 1,
      valueGetter: (params) => {
        return builders.find((obj) => obj.id == params.value)?.name
      }
    },
    {
      field: "agent_id",
      headerName: "Agente de IA",
      flex: 1,
      valueGetter: (params) => {
        return agents.find((obj) => obj.id == params.value)?.name
      }
    },
    {
      field: "actions",
      headerName: "Ações",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <>
          <Tooltip title="Editar">
            <IconButton sx={{ p: 0.25 }} size="small" onClick={() => setEntrypoint({ 
              ...params.row, 
              type: params.row.agent_id ? "agent" : "chatbot"
            })}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.entrypoint === "CHATWEB" && ! params.row.mcdu.startsWith("999") &&
            <Tooltip title="Remover">
              <IconButton sx={{ p: 0.25 }} size="small" color="error" onClick={() => setToDelete(params.row)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        </>
      ),
    },
  ]

  const voiceColumns = [
    { field: "mcdu", headerName: "Número", flex: 1 },
    {
      field: "ivr_module_id",
      headerName: "Vivre",
      flex: 1,
      valueGetter: ({ row }) => {
        if (!row?.mcdu || !voiceIvrs?.length) return ""
        const found = voiceIvrs.find(m =>
          m.mcdu?.some(item => item.mcdu === row.mcdu)
        )
        return found?.name ?? ""
      }
    },
    {
      field: "initial_module",
      headerName: "Módulo inicial",
      flex: 1,
      valueGetter: ({ row }) => {
        if (!row?.mcdu || !voiceIvrs?.length) return ""
        const found = voiceIvrs.find(m =>
          m.mcdu?.some(item => item.mcdu === row.mcdu)
        )
        const mod = found?.modules?.find(item => item.id === row.ivr_module_id)
        return mod?.name ?? ""
      }
    },
    {
      field: "actions",
      headerName: "Ações",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Editar">
          <IconButton sx={{ p: 0.25 }} size="small" onClick={() => setVoiceEntrypoint({ 
            ...params.row, 
          })}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  useEffect(() => {
    const loadMcdus = async () => {
      try {
        setLoading(true)
        const enterpriseId = getSession().profile.enterprise.id
        const promises = [
          api.get(`/api/enterprises/${enterpriseId}/mcdu/chat/`),
          api.get(`/api/enterprises/${enterpriseId}/mcdu/voice/`),
          api.get(`/api/enterprises/${enterpriseId}/ai_agents/`),
          api.get(`/api/getBuilder/`),
          api.get(`/api/enterprises/${enterpriseId}/ura/voice/`),
        ]
        const [ chatResponse, voiceResponse, agentResponse, builderResponse, voiceIvrResponse ] = await Promise.all(promises)
        if (chatResponse.status === 200)
          setChatRows(chatResponse.data)
        if (voiceResponse.status === 200)
          setVoiceRows(voiceResponse.data)
        if (agentResponse.status === 200)
          setAgents(agentResponse.data)
        if (builderResponse.status === 200)
          setBuilders(builderResponse.data.botlist ?? [])
        if (voiceIvrResponse.status === 200)
          setVoiceIvrs(voiceIvrResponse.data ?? [])
      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    loadMcdus()
  }, [])

  const updateEntrypoint = useCallback(async (entrypoint) => {
    try {
      setLoading(true)
      const enterpriseId = getSession().profile.enterprise.id
      if (activeTab === 0) {
        const response = await api.patch(`/api/enterprises/${enterpriseId}/mcdu/chat/${entrypoint.id}/`, {
          ...entrypoint,
          ...(entrypoint.proactive === null && { proactive: 0 })
        })
        if (response.status !== 200) {
          useNotification("Ops", "Algo deu errado, tente novamente.", "danger")
          return
        }
        setEntrypoint(null)
        setChatRows((prev) =>
          prev.map((row) =>
            row.id === entrypoint.id ? { ...row, ...entrypoint } : row
          )
        )
      } else {
        const ivrModuleId = entrypoint.ivr_module_id
        const ivrModule = voiceIvrs.map(ivr => ivr.modules || []).flat().find(mod => mod.id === ivrModuleId)
        if (ivrModule) {
          const payload = {
            mcdu: [entrypoint],
            initialModule: ivrModule
          }
          const response = await api.patch(`/api/enterprises/${enterpriseId}/ura/voice/${ivrModule.ivr_ura_id}/`, payload)
          if (response !== 200) {
            useNotification("Ops", "Algo deu errado, tente novamente.", "danger")
            return
          }
          
          const promises = [
            api.get(`/api/enterprises/${enterpriseId}/mcdu/voice/`),
            api.get(`/api/enterprises/${enterpriseId}/ura/voice/`)
          ]
          const [ voiceResponse, voiceIvrResponse ] = await Promise.all(promises)
          if (voiceResponse === 200)
            setVoiceRows(voiceResponse.data)
          if (voiceIvrResponse === 200)
            setVoiceIvrs(voiceIvrResponse.data ?? [])
          setVoiceEntrypoint(null) 
        }
      } 
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [activeTab])

  const createMcdu = useCallback(async () => {
    const enterpriseId = getSession().profile?.enterprise?.id
    const response = await api.post(`api/enterprises/${enterpriseId}/mcdu/chat/`)
    if (response.status === 201) {
      useNotification("Sucesso", `A entrada ${response.data.mcdu} foi criada com sucesso.`, "success")
      setChatRows((prev) => [response.data, ...prev])
    }
  }, [setChatRows])

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer className={gridClasses.toolbarContainer}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <LoadingButton
          size="small"
          component="label"
          startIcon={<AddCircleIcon />}
          onClick={createMcdu}
        >
          Adicionar
        </LoadingButton>
      </GridToolbarContainer>
    )
  }

  return (
    <>
      <Loading loading={loading} />
  		<Box height="100%" display="flex" gap={2} bgcolor="white">
  	  	{activeTab == 0
  	  		? (
              <ThemeProvider theme={theme}>
    	  				<DataGridPro
    			        rows={chatRows}
    			        columns={chatColumns}
    		          density="compact"
                  components={{ Toolbar: CustomToolbar }}
    			      />
              </ThemeProvider>
  	  		)
  	  		: (
              <ThemeProvider theme={theme}>
    	  				<DataGridPro
    			        rows={voiceRows}
    			        columns={voiceColumns}
    		          density="compact"
    			      />
              </ThemeProvider>
    			)
  	  	}
  		</Box>
      {entrypoint &&
        <Dialog open={entrypoint} onClose={() => setEntrypoint(null)} fullWidth maxWidth="sm">
          <DialogTitle>Entrada</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={3}>
              <Typography display="flex" gap={1}>
                {platformIcon(entrypoint.entrypoint) ?? platformIcon("INSTANTCHAT")}
                {entrypoint.mcdu}
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  label="Tipo"
                  value={entrypoint.type}
                  onChange={(event) => setEntrypoint((prev) => ({ ...prev, type: event.target.value }))}
                >
                  {getSession().profile?.enterprise?.max_ai_agents > 0 &&
                    <MenuItem value="agent">Agente de IA</MenuItem>
                  }
                  <MenuItem value="chatbot">ChatBot</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>
                  {entrypoint.type === "agent"
                    ? "Agente"
                    : "ChatBot"
                  }
                </InputLabel>
                <Select
                  label={entrypoint.type === "agent" ? "Agente" : "ChatBot"}
                  value={entrypoint.type === "agent" ? entrypoint.agent_id : entrypoint.builder_id}
                  onChange={(event) => setEntrypoint((prev) => ({
                    ...prev,
                    agent_id: entrypoint.type === "agent" ? event.target.value : null,
                    builder_id: entrypoint.type === "chatbot" ? event.target.value : null,
                  }))}
                >
                  {entrypoint.type === "agent"
                    ? agents.map((agent) => (
                        <MenuItem value={agent.id}>{agent.name}</MenuItem>
                      ))
                    : builders.map((builder) => (
                        <MenuItem value={builder.id}>{builder.name}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Box px={2} pb={1}>
              <Button onClick={() => setEntrypoint(null)}>Cancelar</Button>
              <Button
                onClick={() => updateEntrypoint(entrypoint)}
                variant="contained"
              >
                Salvar
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      }
      {voiceEntrypoint &&
        <Dialog open={voiceEntrypoint} onClose={() => setVoiceEntrypoint(null)} fullWidth maxWidth="sm">
          <DialogTitle>Entrada</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={3}>
              <Typography display="flex" gap={1}>
                {platformIcon("INSTANTVOICE")}
                {voiceEntrypoint.mcdu}
              </Typography>
              <VoiceIvrSelector 
                entrypoint={voiceEntrypoint}
                setEntrypoint={setVoiceEntrypoint}
                voiceIvrs={voiceIvrs}
                setVoiceIvrs={setVoiceIvrs}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Box px={2} pb={1}>
              <Button onClick={() => setVoiceEntrypoint(null)}>Cancelar</Button>
              <Button
                onClick={() => updateEntrypoint(voiceEntrypoint)}
                variant="contained"
              >
                Salvar
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      }
      {toDelete &&
        <ConfirmationDialogCaptcha
          captcha={toDelete.mcdu}
          description={<Box>Para remover a entrada, digite <span style={{fontWeight: 'bold'}}>{toDelete.mcdu}</span> no campo abaixo.</Box>}
          alternateYes="Confirmar"
          alternateNo="Fechar"
          open={true}
          title="Remover Entrada"
          handleClose={() => setToDelete(null)}
          handleSubmit={() => deleteMcdu(toDelete)}
        />
      }
    </>
  )
}

export default Entrypoints
